import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, RefreshCw, KeyRound, UserCheck, UserX, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, loadAuthToken } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "../components/ui/dialog";

const ROLES = [
  { v: "super_admin",       label: "Super Admin" },
  { v: "central_operator",  label: "Central Operator" },
  { v: "branch_operator",   label: "Branch Operator" },
];

const AdminUsersPage = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [pwReset, setPwReset] = useState(null);

  useEffect(() => {
    if (!loadAuthToken()) { navigate("/admin/login"); return; }
    load();
    // eslint-disable-next-line
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [u, b] = await Promise.all([
        api.get("/admin/users"),
        api.get("/admin/branches"),
      ]);
      setUsers(u.data || []);
      setBranches(b.data || []);
    } catch (err) {
      if (err?.response?.status === 401) navigate("/admin/login");
      else toast.error(err?.response?.data?.detail || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  const toggleActive = async (u) => {
    try {
      await api.patch(`/admin/users/${u.id}`, { active: !u.active });
      toast.success("Updated");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const del = async (u) => {
    if (!window.confirm(`Delete user ${u.email}?`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success("Deleted");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  return (
    <div data-testid="admin-users-page" className="max-w-7xl mx-auto px-4 lg:px-8 py-8 lg:py-10">
      <div className="flex items-center gap-3 mb-6">
        <Button variant="outline" size="sm" onClick={() => navigate("/admin")}><ArrowLeft className="w-4 h-4" /></Button>
        <div className="flex-1">
          <div className="text-xs uppercase tracking-[0.14em] text-teal-700 font-semibold">Super Admin</div>
          <h1 className="mt-1 text-2xl sm:text-3xl font-heading font-semibold text-slate-900">User Management</h1>
        </div>
        <CreateUserDialog open={showCreate} onOpenChange={setShowCreate} branches={branches} onCreated={load} />
      </div>

      <div className="rounded-lg bg-white border border-slate-200 overflow-hidden">
        <div className="flex justify-end p-3 border-b border-slate-100">
          <Button variant="outline" size="sm" onClick={load}><RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /></Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-left">
              <tr>
                <th className="px-4 py-3 font-medium">Name</th>
                <th className="px-4 py-3 font-medium">Email</th>
                <th className="px-4 py-3 font-medium">Role</th>
                <th className="px-4 py-3 font-medium">Branch</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-t border-slate-100" data-testid={`user-row-${u.id}`}>
                  <td className="px-4 py-3 font-medium text-slate-900">{u.name || "—"}</td>
                  <td className="px-4 py-3">{u.email}</td>
                  <td className="px-4 py-3"><Badge className="rounded-full">{ROLES.find(r => r.v === u.role)?.label || u.role}</Badge></td>
                  <td className="px-4 py-3">{branches.find(b => b.id === u.branch_id)?.name_en || "—"}</td>
                  <td className="px-4 py-3"><Badge className={u.active === false ? "bg-red-100 text-red-800" : "bg-green-100 text-green-800"}>{u.active === false ? "Inactive" : "Active"}</Badge></td>
                  <td className="px-4 py-3 text-right space-x-1">
                    {u.role !== "super_admin" && (
                      <>
                        <Button size="sm" variant="outline" onClick={() => setPwReset(u)} data-testid={`reset-${u.id}`}><KeyRound className="w-3.5 h-3.5" /></Button>
                        <Button size="sm" variant="outline" onClick={() => toggleActive(u)} data-testid={`toggle-${u.id}`}>
                          {u.active === false ? <UserCheck className="w-3.5 h-3.5" /> : <UserX className="w-3.5 h-3.5" />}
                        </Button>
                        <Button size="sm" variant="destructive" onClick={() => del(u)} data-testid={`delete-user-${u.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                      </>
                    )}
                    {u.role === "super_admin" && (
                      <span className="text-xs text-slate-500 italic">Server-managed (ADMIN_PASSWORD)</span>
                    )}
                  </td>
                </tr>
              ))}
              {users.length === 0 && !loading && <tr><td colSpan="6" className="px-4 py-10 text-center text-slate-500">No users yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <PasswordResetDialog user={pwReset} onClose={() => setPwReset(null)} onDone={load} />
    </div>
  );
};

const CreateUserDialog = ({ open, onOpenChange, branches, onCreated }) => {
  const [form, setForm] = useState({ email: "", name: "", mobile: "", password: "", role: "branch_operator", branch_id: "" });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = { ...form };
      if (!body.branch_id) delete body.branch_id;
      await api.post("/admin/users", body);
      toast.success("User created");
      onCreated(); onOpenChange(false);
      setForm({ email: "", name: "", mobile: "", password: "", role: "branch_operator", branch_id: "" });
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button className="bg-teal-700 hover:bg-teal-800 text-white" data-testid="create-user-btn"><Plus className="w-4 h-4 mr-2" /> Create User</Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Create User</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3 text-sm">
          <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name" /></div>
          <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email" /></div>
          <div><Label>Mobile</Label><Input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} /></div>
          <div><Label>Password (min 6)</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password" /></div>
          <div>
            <Label>Role</Label>
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
              <SelectContent>{ROLES.map(r => <SelectItem key={r.v} value={r.v}>{r.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {form.role === "branch_operator" && (
            <div>
              <Label>Assign Branch</Label>
              <Select value={form.branch_id} onValueChange={(v) => setForm({ ...form, branch_id: v })}>
                <SelectTrigger data-testid="user-branch"><SelectValue placeholder="Pick branch" /></SelectTrigger>
                <SelectContent>{branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name_en}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" type="button" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={saving} className="bg-teal-700 hover:bg-teal-800 text-white" data-testid="user-submit">{saving ? "…" : "Create"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const PasswordResetDialog = ({ user, onClose, onDone }) => {
  const [pw, setPw] = useState("");
  const [saving, setSaving] = useState(false);
  if (!user) return null;
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch(`/admin/users/${user.id}`, { password: pw });
      toast.success("Password reset");
      onDone(); onClose(); setPw("");
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={!!user} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle>Reset Password — {user.email}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="New password (min 6 chars)" data-testid="new-password-input" />
          <DialogFooter>
            <Button variant="outline" type="button" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={saving} className="bg-teal-700 hover:bg-teal-800 text-white" data-testid="reset-submit">{saving ? "…" : "Reset"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AdminUsersPage;
